import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  DataGridPro,
  GridOverlay,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useMemo } from "react";

export default function DataGridProComponent({
  rows,
  columns,
  pinnedTotalRow,
  isLoading,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  apiRefProp,
  imageNotFound = "NoDeposit", //optional
  serverSidePagination = false,
  onRowClick = (param) => {}, // optional
  rowCount = 0, //optional
  page = 0, //optional
  pageSize = 100, //optional
  onPageChange = (pageNumber) => {}, //optional
  onPageSizeChange = (pageSize) => {}, //optional
  showCustomToolbarColumnsButton = false, //optional
  customToolbarColumnsText = false, //optional

  onRefresh = () => {}, //optional
  showGridRefreshButton = false, //optional

  autoRowHeight = false, //optional — pass true to enable auto row height (e.g. for wrapping text)
  customSx = {}, //optional
}) {
  const pinnedRows = useMemo(
    () => (pinnedTotalRow ? { bottom: [pinnedTotalRow] } : undefined),
    [pinnedTotalRow],
  );

  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;

  const handleCellClick = (row: any, field: string, event: any) => {
    if (field === "action" || field === "__detail_panel_toggle__") {
      event.stopPropagation();
      return;
    }
    onRowClick(row);
  };

  // custom grid button to show/hide columns
  const CustomToolbarColumnsButton = () => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <GridToolbarColumnsButton
          title="Show/Hide Columns"
          sx={customToolbarColumnsText ? { textTransform: "none" } : undefined}
        />
        {/* 👈 Built-in Column Visibility dropdown */}
        {/* 👈 Refresh grid data button */}
        {showGridRefreshButton && (
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "8px",
            }}
          >
            <Tooltip title="Refresh Grid Data">
              <span>
                <IconButton onClick={onRefresh} disabled={isLoading}>
                  <RefreshIcon color={isLoading ? "disabled" : "primary"} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </GridToolbarContainer>
    );
  };

  return (
    <Box sx={{ height: "81vh", width: "100%" }}>
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        pinnedRows={pinnedRows}
        filterMode="server"
        onFilterModelChange={onFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        isRowSelectable={() => false}
        hideFooter={false}
        rowCount={serverSidePagination ? rowCount : undefined}
        pagination
        paginationMode={serverSidePagination ? "server" : "client"}
        page={serverSidePagination ? page : undefined}
        onPageChange={serverSidePagination ? onPageChange : undefined}
        pageSize={serverSidePagination ? pageSize : undefined}
        onPageSizeChange={serverSidePagination ? onPageSizeChange : undefined}
        rowsPerPageOptions={[50, 100, 200, 500]}
        onCellClick={(params, event) =>
          handleCellClick(params.row, params.field, event)
        }
        getRowHeight={autoRowHeight ? () => "auto" : undefined}
        experimentalFeatures={{ rowPinning: true }}
        loading={isLoading}
        localeText={
          customToolbarColumnsText
            ? { toolbarColumns: "Show/Hide Columns" }
            : undefined
        }
        components={{
          NoRowsOverlay: CustomNoRowsOverlay,
          Toolbar: showCustomToolbarColumnsButton
            ? CustomToolbarColumnsButton
            : undefined, //optional
        }}
        componentsProps={{
          noRowsOverlay: {
            isLoading,
            imageNotFound,
          },
        }}
        sx={[
          {
            "& .MuiDataGrid-cell": {
              paddingTop: "12px",
              paddingBottom: "12px",
              minHeight: "52px",
            },
            "& .MuiDataGrid-pinnedRows": {
              backgroundColor: "#f9f9f9",
              fontWeight: "bold",
              pointerEvents: "none",
              border: "solid 1px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9f9f9",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            "& .MuiDataGrid-pinnedRows--bottom": {
              position: "sticky",
              left: 0,
              overflow: "visible",
            },
            "& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row": {
              minWidth: "100%",
              pointerEvents: "auto", // keep row itself clickable
            },
            "& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell": {
              pointerEvents: "none", // block clicks from leaking to below
            },
          },
          customSx,
        ]}
      />
    </Box>
  );
}

const CustomNoRowsOverlay = ({
  isLoading,
  imageNotFound,
}: {
  isLoading: boolean;
  imageNotFound: string;
}) =>
  !isLoading ? (
    <GridOverlay>
      <img
        src={`/images/${imageNotFound}.png`}
        alt="No deposits found"
        style={{ height: 150, marginBottom: 125 }}
      />
    </GridOverlay>
  ) : null;
